import axios, {Method} from 'axios'
import {saveAs} from 'file-saver'
import {downloadStore} from './downloadStore'

export interface DownloadFileOptions {
  url: string
  /** Saved filename including extension, e.g. `New_CIF_Account_Export.csv`. */
  fileName: string
  params?: any
  method?: Method
  /** Request body, for the few exports that POST. */
  data?: any
  /** MIME type for the saved blob. Defaults to text/csv. */
  mimeType?: string
  responseType?: 'blob' | 'arraybuffer'
  /** Fallback message when the API returns an error with no readable body. */
  errorMessage?: string
}

const FILENAME_HEADER = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i

/**
 * The export endpoints are requested as blobs, so an error body arrives as a Blob
 * (or ArrayBuffer) too and has to be read back as text before the API's real message
 * can be surfaced. Without this the user only ever sees a generic failure.
 */
const extractErrorMessage = async (error: any, fallback: string): Promise<string> => {
  const body = error?.response?.data
  try {
    if (body instanceof Blob) {
      const parsed = JSON.parse(await body.text())
      return parsed?.message || parsed?.resDesc || fallback
    }
    if (body instanceof ArrayBuffer) {
      const parsed = JSON.parse(new TextDecoder().decode(body))
      return parsed?.message || parsed?.resDesc || fallback
    }
    return body?.message || body?.resDesc || error?.message || fallback
  } catch {
    return error?.message || fallback
  }
}

/** Prefer the server's Content-Disposition name when CORS exposes it. */
const resolveFileName = (headers: any, fallback: string): string => {
  const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition']
  if (typeof disposition !== 'string') return fallback
  const match = FILENAME_HEADER.exec(disposition)
  if (!match?.[1]) return fallback
  try {
    return decodeURIComponent(match[1].trim())
  } catch {
    return match[1].trim()
  }
}

/**
 * Downloads a file through the shared axios singleton (so the Authorization /
 * X-Fingerprint request interceptors in setup/axios/SetupAxios.ts still apply) while
 * reporting live byte progress into the download tray.
 *
 * The report exports stream from Laravel `response()->streamDownload()`, which sends
 * headers — and therefore a 200 — immediately, then trickles the body out over what
 * can be minutes. That is why an export used to look like it did nothing: the request
 * had "succeeded" long before the file existed. `onDownloadProgress` fires per chunk,
 * so the tray can show bytes accumulating for the whole of that window.
 *
 * `progressEvent.total` is 0 on those endpoints (chunked responses carry no
 * Content-Length), so the tray falls back to an indeterminate bar and a byte counter.
 * Endpoints that do send Content-Length (e.g. the xlsx export, which is built to a
 * temp file first) get a real percentage for free.
 *
 * Resolves when the file has been handed to the browser; rejects on failure so existing
 * per-screen loading flags can still use `.finally()`. A user cancellation resolves
 * rather than rejecting — it is not an error worth alerting about.
 */
export const downloadFile = async (options: DownloadFileOptions): Promise<void> => {
  const {
    url,
    fileName,
    params,
    method = 'get',
    data,
    mimeType = 'text/csv',
    responseType = 'blob',
    errorMessage = 'Unable to export the list. Please try again.',
  } = options

  const source = axios.CancelToken.source()
  const id = downloadStore.add(fileName, () => source.cancel('cancelled-by-user'))

  try {
    const response = await axios.request({
      url,
      method,
      params,
      data,
      responseType,
      cancelToken: source.token,
      onDownloadProgress: (progressEvent: ProgressEvent) => {
        downloadStore.update(id, {
          loaded: progressEvent.loaded ?? 0,
          // lengthComputable is false for the chunked exports; keep total at 0 so the
          // tray knows to stay indeterminate rather than showing a bogus 100%.
          total: progressEvent.lengthComputable ? progressEvent.total : 0,
        })
      },
    })

    const blob = new Blob([response.data], {type: mimeType})
    const savedAs = resolveFileName(response.headers, fileName)

    // saveAs handles the anchor click and revokes the object URL.
    saveAs(blob, savedAs)

    downloadStore.finish(id, 'done', {fileName: savedAs, loaded: blob.size, total: blob.size})
  } catch (error: any) {
    if (axios.isCancel(error)) {
      downloadStore.finish(id, 'cancelled')
      return
    }
    const message = await extractErrorMessage(error, errorMessage)
    downloadStore.finish(id, 'error', {errorMessage: message})
    throw error
  }
}
